import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * A panel that throws must not take the page with it.
 *
 * The Footprint tab went blank because one field was missing from one API path
 * and the panel dereferenced it — a real bug, since fixed, but the damage was
 * out of all proportion to it: the client saw an empty screen with no error and
 * no way to tell whether the scan had failed or the app had.
 *
 * A caught panel says what happened and leaves everything else on the page
 * usable.
 */
export default class PanelBoundary extends Component<
  { children: ReactNode; name: string },
  { message: string | null }
> {
  state = { message: null as string | null };

  static getDerivedStateFromError(err: unknown): { message: string } {
    return { message: err instanceof Error ? err.message : 'unexpected error' };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(`[${this.props.name}]`, err, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.message === null) return this.props.children;
    return (
      <div className="w-full max-w-2xl border border-[#E0574A]/40 bg-[#E0574A]/5 rounded-xl p-5">
        <p className="font-serif text-lg mb-1">{this.props.name} could not be displayed</p>
        <p className="text-sm text-text-secondary">
          Your scan itself is fine. This is my display code failing, not a result. The rest of the page still works,
          and the run is saved under <span className="text-text-primary">My scans &amp; progress</span>.
        </p>
        <p className="font-mono text-xs text-[#E0574A] mt-2 break-words">{this.state.message}</p>
      </div>
    );
  }
}
